import React, { useState, useCallback, useEffect, useMemo, memo } from 'react';
import { useDispatch } from 'react-redux';
import { fetchUserSettings } from '@redux/actions/profileActions';
import { Layout, Menu, Space, Drawer, Button, Tabs, Divider, Typography } from 'antd';
import {
  PhoneOutlined,
  ApiOutlined,
  MenuOutlined,
  DollarOutlined,
  CreditCardOutlined,
  UserOutlined,
  QuestionOutlined,
  ReadOutlined,
  VideoCameraOutlined,
  TeamOutlined,
  CloseCircleOutlined,
  CustomerServiceOutlined,
  // BugOutlined,
  FileTextOutlined,
  HistoryOutlined,
  LinkOutlined,
  FileDoneOutlined,
  ShopOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft';
import { isEmpty } from 'lodash';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Avatar from 'react-avatar';
import config from 'react-global-configuration';

import { isCredibleUser } from '@utils/utils';
import './Header.scss';
import FeatureRequestModal from '@Components/Modals/FeatureRequestModal';
import { useMedia } from '@hooks/useMedia';
import ThemeToggle from './components/ThemeToggle/ThemeToggle';
import Href from '@shared/Components/Href';
import { useTheme } from '@context/ThemeContext';
import { getBrandBaseUrl } from '@utils/updateBrandAssets';
import StatusIndicator from '@Layout//Header/components/StatusIndicator/StatusIndicator';
import modalService from '@utils/modalService';
import messageService from '@utils/messageService';
import { Copy, KeyOutlined, Lifebuoy, Queue, SquaresFour } from '@assets/icons/ui';
import { NOT_ROUTES } from '@shared/constants/header.constants';
import { localizeHeaderLabel } from '@shared/data/headerLocalization';
import { showUserName, getActiveKey, getHeaderMenuRoutes } from '@utils/header';
import { useGuardedNavigate } from '@hooks/useUnsavedChangesWarning';
import { buildWhatsAppSupportUrl } from '@utils/whatsapp';
import { getUrlLanguageParam } from '@utils/languageContract';

const { Header } = Layout;
const { Text } = Typography;

const HeaderComponent = ({ auth, isGodMode }) => {
  const navigate = useGuardedNavigate();
  const location = useLocation();
  const { isDesktop } = useMedia();
  const dispatch = useDispatch();
  const { isDarkMode } = useTheme();

  const { profile = {} } = auth;
  const { avatar, name, email, roles = [] } = profile;
  const isTenant = roles.includes('tenant');
  const isAuthenticated = auth.authenticated;

  const { t, i18n } = useTranslation();
  const whatsappNumber = config.get('whatsappNumber');
  const company = config.get('company');

  const whatsappSupportUrl = useMemo(() => buildWhatsAppSupportUrl({
    whatsappNumber,
    company,
    email,
  }), [whatsappNumber, company, email]);

  const [featureRequestOpen, setFeatureRequestOpen] = useState(false);
  const [isHelpOpened, setIsHelpOpened] = useState(false);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const baseUrl = useMemo(() => getBrandBaseUrl(), []);

  const { logoUrl, fallbackLogoUrl } = useMemo(() => {
    const getLogoUrl = (fileName) => baseUrl === '/' ? `/${fileName}` : `${baseUrl}/${fileName}`;
    return {
      logoUrl: getLogoUrl(isDarkMode ? 'logo-dark.svg' : 'logo-light.svg'),
      fallbackLogoUrl: getLogoUrl('logo.svg')
    };
  }, [baseUrl, isDarkMode]);

  const language = useMemo(
    () => getUrlLanguageParam(i18n.resolvedLanguage || i18n.language),
    [i18n.language, i18n.resolvedLanguage],
  );

  const navigateToPath = useCallback((path, event) => {
    if (event?.metaKey || event?.ctrlKey) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(path);
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUserSettings());
    }
  }, [dispatch, isAuthenticated]);

  const onHelpClick = useCallback(() => {
    if (typeof window.Chatra !== 'function') return;

    if (isHelpOpened) {
      window.Chatra('closeChat');
    } else {
      window.Chatra('updateIntegrationData', { email });
      window.Chatra('openChat', true);
    }
    setIsHelpOpened(prev => !prev);
  }, [isHelpOpened, email]);

  const handleLogout = useCallback(() => {
    modalService.confirm({
      title: t('title.logOut'),
      content: t('title.areYouSure'),
      okText: t('action.yes'),
      okButtonProps: { danger: true },
      cancelText: t('action.no'),
      onOk: () => auth.logout(),
    });
  }, [t, auth]);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const toggleFeatureRequest = useCallback(() => setFeatureRequestOpen(prev => !prev), []);

  const handleCopyEmail = useCallback((e) => {
    e?.stopPropagation();
    if (!email) return;

    navigator.clipboard.writeText(email);
    messageService.success(t('message.copied'));
  }, [email, t]);

  const getItem = useCallback((label, key, icon, children, type, uniqueKey) => {
    let translatedLabel = typeof label === 'string' ? localizeHeaderLabel(t, label) : label;
    const originalTextLabel = translatedLabel;
    const originalKey = key;

    if (isEmpty(children)) {
      const isExternal = key && (key.startsWith('https://') || key.startsWith('http://'));
      if (!isExternal && key && !NOT_ROUTES.has(key)) {
        if (language) {
          key = key + (key.includes('?') ? '&' : '?') + `ln=${language}`;
        }
        translatedLabel = <Href href={key.startsWith('/') ? key : `/${key}`}>{translatedLabel}</Href>;
      }
    }

    if (type === 'group' && icon) {
      translatedLabel = <><span style={{ marginRight: 8 }}>{icon}</span>{translatedLabel}&nbsp;</>;
    }

    const iconElement = icon ? (typeof icon === 'string' ? <img src={icon} alt={String(translatedLabel)} /> : icon) : null;

    return {
      key: uniqueKey || originalKey,
      navigationKey: key,
      icon: iconElement,
      children,
      items: children,
      label: translatedLabel,
      type,
      title: translatedLabel,
      searchLabel: originalTextLabel
    };
  }, [t, language]);

  const profileHeader = (
    <Space size={0} orientation='vertical'>
      <Text strong>{name || showUserName(email)}</Text>
      <Text
        type='secondary'
        className='text-sm'
        onClick={handleCopyEmail}
        copyable={email ? {
          text: email,
          icon: <Copy />,
          tooltips: [t('title.copyToClipboard'), t('message.copied')],
        } : false}
      >
        {email}
      </Text>
    </Space>
  );

  const profileMenu = useMemo(() => [
    {
      key: 'profile-items',
      label: (
        isDesktop ? (
          <Avatar
            className='profile-avatar'
            src={avatar}
            email={!avatar ? email : null}
            name={!avatar && !email ? name : showUserName(email)}
            size='32'
            round
          />
        ) : profileHeader
      ),
      children: [
        ...(isDesktop
          ? [
            {
              key: 'profile-header',
              type: 'group',
              label: profileHeader,
            },
            { type: 'divider' },
          ]
          : []),

        getItem(t('title.account'), 'account', <UserOutlined />),
        ...(isTenant
          ? []
          : [getItem(t('title.billingInformation'), 'billing', <CreditCardOutlined />)]),
        { type: 'divider' },
        {
          key: 'logout',
          type: 'group',
          label: (
            <Button
              size='small'
              block
              onClick={(e) => {
                e.stopPropagation();
                handleLogout();
              }}
            >
              {t('title.logOut')}
            </Button>
          ),
        },
      ],

    }
  ], [avatar, email, name, isTenant, t, isDesktop, profileHeader, handleLogout]);

  const apiMenu = useMemo(() => [
    getItem(t('title.apiDocs'), 'api-docs', <FileTextOutlined />),
    getItem(t('title.apiKey'), 'api-key', <KeyOutlined style={{ width: 14, height: 14 }} />),
    getItem(t('title.apiUsage'), 'usage-history', <HistoryOutlined />),
    getItem(t('title.integrations'), 'integrations', <LinkOutlined />),
  ], [t, getItem]);

  const businessesMenu = useMemo(() => [
    getItem('Catalog', 'businesses', <FileDoneOutlined />),
    getItem('Export History', 'businesses/export/history', <HistoryOutlined />),
    getItem('Similar Companies', 'businesses/similar', <ShopOutlined />),
  ], [getItem]);

  const dataMenu = useMemo(() => [
    getItem('Business Catalog', 'businesses', <ShopOutlined />),
    getItem('Export History', 'businesses/export/history', <HistoryOutlined />),
    getItem('Dataset Marketplace', 'datasets', <FileDoneOutlined />),
    getItem('Similar Companies', 'businesses/similar', <ShopOutlined />),
  ], [getItem]);

  const helpMenu = useMemo(() => [
    getItem('F.A.Q.', config.get('faqUrl'), <QuestionOutlined />),
    getItem(t('title.tutorials'), config.get('tutorialsUrl'), <ReadOutlined />),
    getItem(t('title.videos'), config.get('videosUrl'), <VideoCameraOutlined />),
    getItem(t('title.askCommunity'), config.get('communityUrl'), <TeamOutlined />),
    getItem(t('title.support'), 'support', isHelpOpened ? <CloseCircleOutlined /> : <CustomerServiceOutlined />),
    ...(whatsappSupportUrl
      ? [getItem(
        t('title.chatOnWhatsApp'),
        whatsappSupportUrl,
        <WhatsAppOutlined />,
        undefined,
        undefined,
        'whatsapp-support'
      )]
      : []),
    ...(isCredibleUser() ? [getItem(t('title.schedule'), config.get('scheduleCallUrl'), <PhoneOutlined />)] : []),
    // getItem(t('title.reportProblem'), 'report', <BugOutlined />),
    getItem(t('title.pricing'), config.get('pricingUrl'), <DollarOutlined />),
  ], [t, isHelpOpened, getItem, whatsappSupportUrl]);

  const configuredHeaderMenuRoutes = config.get('headerMenuRoutes');
  const headerMenuRoutes = useMemo(() => getHeaderMenuRoutes(configuredHeaderMenuRoutes), [configuredHeaderMenuRoutes]);

  const productMenuRegistry = useMemo(() => ({
    '/services': () => getItem(t('title.services'), '/services', <SquaresFour />),
    '/data': () => getItem('Data', '/data', <FileDoneOutlined />, dataMenu),
    '/businesses': () => getItem(
      'Businesses',
      '/businesses',
      <ShopOutlined />,
      headerMenuRoutes.includes('/businesses/export/history') ? undefined : businessesMenu
    ),
    '/businesses/export/history': () => getItem('Export History', '/businesses/export/history', <HistoryOutlined />),
    '/datasets': () => getItem('Datasets', '/datasets', <FileDoneOutlined />),
    '/tasks': () => getItem(t('title.tasks'), '/tasks', <Queue />),
  }), [t, businessesMenu, dataMenu, headerMenuRoutes, getItem]);

  const productMenu = useMemo(() =>
    headerMenuRoutes
      .map(route => productMenuRegistry[route]?.())
      .filter(Boolean),
  [headerMenuRoutes, productMenuRegistry]);

  const rootMenu = useMemo(() => [
    ...productMenu,
    getItem('API & ' + t('title.integrations'), 'api-integrations', <ApiOutlined />, apiMenu),
    getItem(t('title.help'), 'help', <Lifebuoy />, helpMenu)
  ], [t, apiMenu, helpMenu, productMenu, getItem]);

  const activeKey = useMemo(() => getActiveKey(location.pathname, headerMenuRoutes), [location.pathname, headerMenuRoutes]);

  const items = useMemo(() => rootMenu.map(item => ({
    key: item.key,
    label: <span className='menu-item'>{item.icon}{item.searchLabel}</span>,
  })), [rootMenu]);
  const mainMenuLabel = t('title.mainMenu');

  const [menuStack, setMenuStack] = useState(() => [getItem(mainMenuLabel, 'root', null, rootMenu)]);

  useEffect(() => {
    setMenuStack([getItem(mainMenuLabel, 'root', null, rootMenu)]);
  }, [rootMenu, getItem, mainMenuLabel]);

  const currentMenu = menuStack[menuStack.length - 1];

  const handleHeaderMenuClick = useCallback((menuKey, event) => {
    const selectedMenu = rootMenu.find(item => item.key === menuKey);

    if (selectedMenu?.items?.length) {
      setMenuStack([getItem(mainMenuLabel, 'root', null, rootMenu), selectedMenu]);
      setDrawerOpen(true);
      return;
    }

    if (selectedMenu?.navigationKey) {
      const path = selectedMenu.navigationKey.startsWith('/')
        ? selectedMenu.navigationKey
        : `/${selectedMenu.navigationKey}`;
      navigateToPath(path, event);
    }
  }, [navigateToPath, rootMenu, getItem, mainMenuLabel]);

  const handleLogin = () => {
    const redirect = window.location.pathname + window.location.search;
    navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
  };

  const handleSignup = () => {
    const redirect = window.location.pathname + window.location.search;
    navigate(`/signup?redirect=${encodeURIComponent(redirect)}`);
  };

  const goBack = useCallback(() => {
    setMenuStack(prev => prev.length > 1
      ? prev.slice(0, -1)
      : [getItem(mainMenuLabel, 'root', null, rootMenu)]
    );
  }, [rootMenu, getItem, mainMenuLabel]);

  const handleBackKeyDown = useCallback((event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    goBack();
  }, [goBack]);

  const handleMenuClick = useCallback(({ key, domEvent }) => {
    const selectedItem = currentMenu.items.find(item => item.key === key);

    if (selectedItem?.items?.length > 0) {
      setMenuStack(prev => [...prev, selectedItem]);
      return;
    }

    const navigationKey = selectedItem?.navigationKey || key;

    if (navigationKey.startsWith('https://') || navigationKey.startsWith('http://')) {
      window.open(navigationKey, '_blank', 'noopener,noreferrer');
    } else if (navigationKey === 'support') {
      onHelpClick();
    } else if (navigationKey === 'report') {
      toggleFeatureRequest();
    } else if (navigationKey === 'logout') {
      handleLogout();
    } else {
      const path = navigationKey.startsWith('/') ? navigationKey : `/${navigationKey}`;
      navigateToPath(path, domEvent);
    }
    setSearchTerm('');
    setDrawerOpen(false);
  }, [currentMenu.items, navigateToPath, onHelpClick, toggleFeatureRequest, handleLogout]);

  const mappedMenuItems = useMemo(() =>
    currentMenu.items.map(item => {
      if (!item?.key) return null;
      return {
        key: item.key,
        icon: item.icon ? React.cloneElement(item.icon) : null,
        label: item.searchLabel || item.label,
        disabled: !!searchTerm,
      };
    }).filter(Boolean),
  [currentMenu.items, searchTerm]);

  const drawerSelectedKeys = useMemo(() => {
    if (!currentMenu.items) return [];
    const currentPath = location.pathname.replace(/^\/+/, '');
    const pathRoot = currentPath.split('/')[0];
    const getNavKey = item => (item?.navigationKey || item?.key || '').split('?')[0].replace(/^\/+/, '');
    const exactMatch = currentMenu.items.find(item => getNavKey(item) === currentPath);
    const match = exactMatch || currentMenu.items.find(item => getNavKey(item).split('/')[0] === pathRoot);

    return match ? [match.key] : [];
  }, [location.pathname, currentMenu.items]);

  const handleLogoError = useCallback((e) => {
    e.target.onerror = null;
    e.target.src = fallbackLogoUrl;
  }, [fallbackLogoUrl]);

  const drawerTitle = useMemo(() =>
    menuStack.length > 1 ? (
      <Space
        align='center'
        className='drawer-title'
        role='button'
        tabIndex={0}
        aria-label={t('title.back')}
        onClick={goBack}
        onKeyDown={handleBackKeyDown}
      >
        <span className='back-icon'><FontAwesomeIcon icon={faChevronLeft} /></span>
        <h4 className='h4 bold'>{currentMenu.label}</h4>
      </Space>
    ) : <h4 className='h4 bold'>{t('title.menu')}</h4>,
  [menuStack.length, currentMenu.label, goBack, handleBackKeyDown, t]
  );

  const profileActiveKey = useMemo(() => {
    if (location.pathname.startsWith('/account')) return 'account';
    if (location.pathname.startsWith('/billing')) return 'billing';
    return undefined;
  }, [location.pathname]);

  const accountMenu = useCallback(() => {
    const items = profileMenu.map(item => ({
      ...item,
      children: item.children?.map(child =>
        child.searchLabel ? { ...child, label: child.searchLabel } : child
      ),
    }));

    return (
      <Menu
        className='account-menu'
        theme={isDarkMode ? 'dark' : 'light'}
        mode={isDesktop ? 'horizontal' : 'inline'}
        items={items}
        selectedKeys={profileActiveKey ? [profileActiveKey] : []}
        onClick={handleMenuClick}
      />
    );
  }, [isDarkMode, isDesktop, profileMenu, handleMenuClick, profileActiveKey]);

  return (
    <Header theme='light' className={`header ${isGodMode ? 'god-mode-active' : ''}`}>
      <div className='container'>
        <Space size={32} align='center'>
          <Link to='/' className='logo'>
            <img
              src={logoUrl}
              onError={handleLogoError}
              alt={t('title.logo')}
              className='logo'
            />
          </Link>
          {isDesktop && (
            <Tabs
              key={activeKey || 'null'}
              activeKey={activeKey}
              items={items}
              onTabClick={handleHeaderMenuClick}
              className='menu'
            />
          )}
        </Space>
        <Space size={isDesktop ? 8 : 16} align='center' className='right-controls'>
          <StatusIndicator />
          {isDesktop && <Space align={'center'}>
            <Divider size='large' orientation='vertical' />
            <ThemeToggle />
            <Divider size='large' orientation='vertical' />
          </Space>
          }
          {!isDesktop && (
            <Button
              size='large'
              type='text'
              className='burger-menu'
              aria-label={t('title.menu')}
              onClick={openDrawer}
            >
              <MenuOutlined />
            </Button>
          )}
          {isDesktop && (
            isAuthenticated ? accountMenu() : (
              <Space size={8}>
                <Button onClick={handleLogin}>
                  {t('title.logIn')}
                </Button>
                <Button type='primary' onClick={handleSignup}>
                  {t('title.signUp')}
                </Button>
              </Space>
            )
          )}
        </Space>
      </div>

      <Drawer
        title={drawerTitle}
        placement={isDesktop ? 'left' : 'right'}
        onClose={closeDrawer}
        open={isDrawerOpen}
      >
        <Space orientation='vertical' className='w-100'>
          {!isDesktop && currentMenu.key === 'root' && (
            isAuthenticated ? accountMenu() : (
              <Space orientation='vertical' className='w-100' size={8}>
                <Button block onClick={() => { setDrawerOpen(false); handleLogin(); }}>
                  {t('title.logIn')}
                </Button>
                <Button block type='primary' onClick={() => { setDrawerOpen(false); handleSignup(); }}>
                  {t('title.signUp')}
                </Button>
              </Space>
            )
          )}
          <Menu
            className={`main-menu ${(currentMenu.key === 'root' || currentMenu.key === 'sources') ? 'brands' : ''}`}
            mode='inline'
            openKeys={[]}
            selectedKeys={drawerSelectedKeys}
            items={mappedMenuItems}
            onClick={handleMenuClick}
          />
          {!isDesktop && currentMenu.key === 'root' && <ThemeToggle />}
        </Space>
      </Drawer>

      <FeatureRequestModal bug open={featureRequestOpen} onChange={setFeatureRequestOpen} />
    </Header>
  );
};

HeaderComponent.propTypes = {
  auth: PropTypes.object.isRequired,
  isGodMode: PropTypes.bool,
};

export default memo(HeaderComponent);
